// components/Chat/zenoCards.js
//
// Zeno emits resource specs (avatar / agent / workflow) as fenced blocks:
//
//   ```zeno:card
//   { "type": "avatar", "name": "...", "sections": [ ... ] }
//   ```
//
// The web app parses those blocks and renders a visual <ZenoResourceCard>
// instead of the raw JSON — mirroring Support's `zenit:handoff` card mechanism.
// The prompt contract that makes Zeno emit these lives in the backend migration
// v180_zeno_build_card_output_contract (zenitailab). The wizard "Send to chat"
// also emits one directly so the first card shows without waiting on the model.

const CARD_RE = /```zeno:card\s*(\{[\s\S]*?\})\s*```/gi;

const VALID_TYPES = new Set(['avatar', 'agent', 'workflow']);

/**
 * Extract zeno:card blocks from a message's text.
 * @returns {{ cleanText: string, cards: Array<object> }}
 *   cleanText: the text with valid card blocks removed.
 *   cards: parsed card objects (each has a `type`).
 * Invalid / non-card blocks are left untouched in cleanText.
 */
export function parseZenoCards(text) {
  if (!text || typeof text !== 'string') return { cleanText: text || '', cards: [] };

  const cards = [];
  const cleanText = text
    .replace(CARD_RE, (match, json) => {
      try {
        const obj = JSON.parse(json.trim());
        if (obj && typeof obj === 'object' && VALID_TYPES.has(obj.type)) {
          cards.push(obj);
          return ''; // hide the raw block; the card replaces it
        }
      } catch {
        /* malformed (e.g. mid-stream) — leave the block as-is */
      }
      return match;
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return { cleanText, cards };
}

/** Build a ```zeno:card``` block string from a card object (used by the wizard). */
export function buildZenoCardBlock(card) {
  return '```zeno:card\n' + JSON.stringify(card, null, 2) + '\n```';
}
